import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Button, Card, Chip, Separator, Skeleton, Table } from "@heroui/react";
import { Icon } from "@iconify/react";
import QRCode from "react-qr-code";
import { dt } from "@/shared/helper/datetime";

import { useAccumulatorById } from "@/features/accumulators/use-accumulators.ts";
import { AccumulatorDetailOrder } from "@/features/accumulators/types.ts";
import { OrderStatus } from "@/features/order/types.ts";
import { accumulatorTools } from "@/shared/helper/accumulator.ts";
import { orderTools } from "@/shared/helper/order.ts";
import { PageLayout } from "@/shared/components/page-layout.ui.tsx";

export const Route = createFileRoute(
  "/dashboard/order/accumulators/$accumulatorId/",
)({
  component: RouteComponent,
});

const orderStatusColorMap: Record<
  OrderStatus,
  "default" | "accent" | "success" | "warning" | "danger"
> = {
  created: "default",
  cancelled: "danger",
  accepted: "accent",
  going_to_sender: "accent",
  decided: "danger",
  submitted: "accent",
  accounted: "warning",
  checked: "warning",
  revision: "warning",
  dispatched: "warning",
  transit: "accent",
  arrived: "accent",
  assigned: "accent",
  delivers_recipient: "accent",
  completed: "success",
};

function InfoRow({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs text-neutral-400">{label}</span>
      <span className="text-sm">{value || "—"}</span>
    </div>
  );
}

function formatDateTime(value?: string | null) {
  if (!value) return "—";
  const date = dt(value);

  if (!date.isValid() || date.year() <= 1) return "—";

  return date.format("DD.MM.YYYY HH:mm");
}

function formatWeight(value?: number | null) {
  return `${(value ?? 0).toFixed(2)} кг`;
}

function OrderCard({ order }: { order: AccumulatorDetailOrder }) {
  return (
    <Card>
      <Card.Header className="pb-0 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Icon className="size-4 text-neutral-400" icon="solar:box-outline" />
          <span className="font-semibold text-sm">{order.number}</span>
        </div>
        <Chip
          color={orderStatusColorMap[order.status] ?? "default"}
          size="sm"
          variant="soft"
        >
          {orderTools.status.toString(order.status)}
        </Chip>
      </Card.Header>
      <Separator className="mt-2" />
      <Card.Content className="flex flex-col gap-4 p-0">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 px-4 pt-4">
          <InfoRow label="Откуда" value={order.fromAddress} />
          <InfoRow label="Куда" value={order.toAddress} />
          <InfoRow label="Мест" value={String(order.places)} />
          <InfoRow label="Вес" value={formatWeight(order.weight)} />
          {order.description && (
            <div className="col-span-2 lg:col-span-4">
              <InfoRow label="Описание" value={order.description} />
            </div>
          )}
        </div>
        {order.packages.length === 0 ? (
          <p className="text-sm text-neutral-400 p-4">Нет посылок</p>
        ) : (
          <Table.Root>
            <Table.Content aria-label={`Посылки заказа ${order.number}`}>
              <Table.Header>
                <Table.Column>Название</Table.Column>
                <Table.Column>Штрихкод</Table.Column>
                <Table.Column>Сканирование</Table.Column>
              </Table.Header>
              <Table.Body items={order.packages.map((p, i) => ({ ...p, i }))}>
                {(pkg) => (
                  <Table.Row id={String(pkg.i)}>
                    <Table.Cell className="text-sm">{pkg.title}</Table.Cell>
                    <Table.Cell className="text-xs font-mono break-all select-all">
                      {pkg.barcode || "—"}
                    </Table.Cell>
                    <Table.Cell>
                      <Chip
                        color={pkg.isScanned ? "success" : "default"}
                        size="sm"
                        variant="soft"
                      >
                        {pkg.isScanned ? "Отсканирована" : "Не отсканирована"}
                      </Chip>
                    </Table.Cell>
                  </Table.Row>
                )}
              </Table.Body>
            </Table.Content>
          </Table.Root>
        )}
      </Card.Content>
    </Card>
  );
}

function RouteComponent() {
  const { accumulatorId } = Route.useParams();
  const navigate = useNavigate();

  const { data, isLoading, isError } = useAccumulatorById({ accumulatorId });
  const accumulator = data?.data;

  return (
    <PageLayout
      title={
        <div className="flex items-center gap-2">
          <Button
            isIconOnly
            size="sm"
            variant="ghost"
            onPress={() => navigate({ to: "/dashboard/order/accumulators" })}
          >
            <Icon className="size-5" icon="solar:arrow-left-outline" />
          </Button>
          <span>Накопитель {accumulator?.number ?? accumulatorId}</span>
          {accumulator && (
            <Chip
              color={accumulatorTools.status.color(accumulator.status)}
              size="sm"
              variant="soft"
            >
              {accumulatorTools.status.toString(accumulator.status)}
            </Chip>
          )}
        </div>
      }
    >
      {isError && (
        <div className="flex flex-col items-center justify-center py-16 gap-2 text-neutral-400">
          <Icon className="size-10" icon="solar:danger-circle-outline" />
          <p>Накопитель не найден</p>
        </div>
      )}

      {isLoading && (
        <div className="flex flex-col gap-4 mt-4">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="p-4">
              <Skeleton className="h-5 w-40 rounded mb-4" />
              <Skeleton className="h-4 w-full rounded" />
            </Card>
          ))}
        </div>
      )}

      {accumulator && (
        <div className="flex flex-col gap-4 mt-4">
          <Card>
            <Card.Header className="pb-0 flex items-center gap-2">
              <Icon
                className="size-4 text-neutral-400"
                icon="solar:clipboard-list-outline"
              />
              <span className="font-semibold text-sm">Манифест</span>
            </Card.Header>
            <Separator className="mt-2" />
            <Card.Content className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 flex-1">
                <InfoRow label="Откуда" value={accumulator.fromCityName} />
                <InfoRow label="Куда" value={accumulator.toCityName} />
                <InfoRow label="Накладная" value={accumulator.waybillNumber} />
                <InfoRow
                  label="Дата создания"
                  value={formatDateTime(accumulator.createdAt)}
                />
                <InfoRow
                  label="Плановая отгрузка"
                  value={formatDateTime(accumulator.pickUpTime)}
                />
                <InfoRow
                  label="Плановое получение"
                  value={formatDateTime(accumulator.receiveTime)}
                />
                <InfoRow
                  label="Количество заказов"
                  value={String(accumulator.orders?.length ?? 0)}
                />
                <InfoRow
                  label="Количество мест"
                  value={String(accumulator.places ?? 0)}
                />
                <InfoRow
                  label="Объёмный вес"
                  value={formatWeight(accumulator.volumetricWeight)}
                />
              </div>
              {accumulator.qr && accumulator.waybillNumber && (
                <div className="flex flex-col items-center gap-2 self-center lg:self-start">
                  <div className="bg-white p-3 rounded-xl border border-neutral-200">
                    <QRCode size={120} value={accumulator.qr} />
                  </div>
                  <span className="text-xs text-neutral-400">
                    {accumulator.waybillNumber}
                  </span>
                </div>
              )}
            </Card.Content>
          </Card>

          {(accumulator.orders ?? []).length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 gap-2 text-neutral-400">
              <Icon className="size-10" icon="solar:box-outline" />
              <p>В накопителе нет заказов</p>
            </div>
          ) : (
            accumulator.orders.map((order) => (
              <OrderCard key={order.number} order={order} />
            ))
          )}
        </div>
      )}
    </PageLayout>
  );
}
